// src/features/products/components/BulkUploadResultModal.tsx

import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, AlertTriangle, ArrowRight, FileText, Download, Upload, Package, Sparkles, Truck, X as XIcon } from 'lucide-react';
import { clsx } from 'clsx';
import type { BulkUploadResult } from '../services/productsApi';

interface BulkUploadResultModalProps {
    isOpen: boolean;
    result: BulkUploadResult | null;
    onClose: () => void;
    onViewErrors: (uploadId: string) => void;
    onGoToProducts: () => void;
    onUploadAnother: () => void;
    onDownloadReport: (uploadId: string) => void;
}

export function BulkUploadResultModal({
    isOpen,
    result,
    onClose,
    onViewErrors,
    onGoToProducts,
    onUploadAnother,
    onDownloadReport,
}: BulkUploadResultModalProps) {
    if (!result) return null;

    const hasErrors = result.hasErrors || result.errorCount > 0;
    const successRate = result.totalRows > 0
        ? ((result.successCount / result.totalRows) * 100).toFixed(1)
        : '0';

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 20 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        onClick={e => e.stopPropagation()}
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
                    >
                        {/* Close Button */}
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 z-10 p-2 rounded-xl bg-white/20 text-white hover:bg-white/30 transition-colors"
                        >
                            <XIcon size={18} />
                        </button>

                        {/* Header */}
                        <div className={clsx(
                            'px-8 py-8 text-center',
                            hasErrors ? 'bg-gradient-to-br from-amber-500 to-orange-600' : 'bg-gradient-to-br from-emerald-500 to-teal-600'
                        )}>
                            <motion.div
                                initial={{ scale: 0, rotate: -30 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.2 }}
                                className={clsx(
                                    'w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4',
                                    'bg-white/20 backdrop-blur-sm'
                                )}
                            >
                                {hasErrors ? (
                                    <AlertTriangle size={40} className="text-white" />
                                ) : (
                                    <Sparkles size={40} className="text-white" />
                                )}
                            </motion.div>
                            <h2 className="text-2xl font-black text-white">
                                {hasErrors ? 'Import Complete with Errors' : 'Import Successful! 🎉'}
                            </h2>
                            <p className="text-white/80 text-sm mt-1 font-medium">
                                {hasErrors
                                    ? `${result.successCount} succeeded, ${result.errorCount} failed out of ${result.totalRows}`
                                    : `All ${result.successCount} products imported successfully`}
                            </p>
                        </div>

                        {/* Body */}
                        <div className="p-8">
                            {/* Stats Grid */}
                            <div className="grid grid-cols-3 gap-3 mb-6">
                                <StatBox
                                    label="Total"
                                    value={result.totalRows}
                                    color="text-slate-700"
                                    bg="bg-slate-50"
                                    border="border-slate-200"
                                />
                                <StatBox
                                    label="Success"
                                    value={result.successCount}
                                    color="text-emerald-600"
                                    bg="bg-emerald-50"
                                    border="border-emerald-200"
                                />
                                <StatBox
                                    label="Failed"
                                    value={result.errorCount}
                                    color={result.errorCount > 0 ? 'text-rose-600' : 'text-slate-400'}
                                    bg={result.errorCount > 0 ? 'bg-rose-50' : 'bg-slate-50'}
                                    border={result.errorCount > 0 ? 'border-rose-200' : 'border-slate-200'}
                                />
                            </div>

                            {/* Success Rate Bar */}
                            <div className="mb-6">
                                <div className="flex justify-between text-xs mb-2">
                                    <span className="text-slate-500 font-medium">Success Rate</span>
                                    <span className={clsx('font-bold', hasErrors ? 'text-amber-600' : 'text-emerald-600')}>
                                        {successRate}%
                                    </span>
                                </div>
                                <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${successRate}%` }}
                                        transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
                                        className={clsx(
                                            'h-3 rounded-full',
                                            hasErrors ? 'bg-gradient-to-r from-amber-400 to-orange-500' : 'bg-gradient-to-r from-emerald-400 to-teal-500'
                                        )}
                                    />
                                </div>
                            </div>

                            {/* New Categories Alert */}
                            {(result.categoriesCreated ?? 0) > 0 && (
                                <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-2xl mb-4">
                                    <div className="p-2 bg-blue-100 rounded-xl">
                                        <Package size={18} className="text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-blue-700">
                                            {result.categoriesCreated} new categor{result.categoriesCreated !== 1 ? 'ies' : 'y'} auto-created
                                        </p>
                                        <p className="text-xs text-blue-500">Categories were created automatically from your spreadsheet</p>
                                    </div>
                                </div>
                            )}

                            {/* New Vendors Alert */}
                            {(result.vendorsCreated ?? 0) > 0 && (
                                <div className="flex items-center gap-3 p-4 bg-teal-50 border border-teal-200 rounded-2xl mb-4">
                                    <div className="p-2 bg-teal-100 rounded-xl">
                                        <Truck size={18} className="text-teal-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-teal-700">
                                            {result.vendorsCreated} new vendor{result.vendorsCreated !== 1 ? 's' : ''} auto-created & linked
                                        </p>
                                        <p className="text-xs text-teal-600">Suppliers were automatically added to your active Vendor Directory</p>
                                    </div>
                                </div>
                            )}

                            {/* Skipped Alert */}
                            {(result.skippedCount ?? 0) > 0 && (
                                <div className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-2xl mb-4">
                                    <div className="p-2 bg-slate-100 rounded-xl">
                                        <CheckCircle size={18} className="text-slate-500" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-slate-700">
                                            {result.skippedCount} duplicate{result.skippedCount !== 1 ? 's' : ''} skipped
                                        </p>
                                        <p className="text-xs text-slate-500">These products already exist in your catalog</p>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="space-y-3">
                                {hasErrors && (
                                    <>
                                        <button
                                            onClick={() => onViewErrors(result.uploadId)}
                                            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl hover:from-amber-600 hover:to-orange-700 font-bold text-sm shadow-lg shadow-amber-500/25 transition-all"
                                        >
                                            <FileText size={18} />
                                            View Failed Products ({result.errorCount})
                                            <ArrowRight size={16} className="ml-auto" />
                                        </button>
                                        <button
                                            onClick={() => onDownloadReport(result.uploadId)}
                                            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50 font-semibold text-sm text-slate-700 transition-all"
                                        >
                                            <Download size={16} />
                                            Download Error Report (Excel)
                                        </button>
                                    </>
                                )}
                                <button
                                    onClick={onGoToProducts}
                                    className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-xl hover:from-slate-800 hover:to-slate-900 font-bold text-sm shadow-lg transition-all"
                                >
                                    <Package size={16} />
                                    Go to Products
                                </button>
                                <button
                                    onClick={onUploadAnother}
                                    className="w-full flex items-center justify-center gap-2 px-6 py-2.5 text-slate-500 hover:text-slate-700 text-sm font-medium transition-colors"
                                >
                                    <Upload size={14} />
                                    Upload Another File
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function StatBox({ label, value, color, bg, border }: {
    label: string; value: number; color: string; bg: string; border: string;
}) {
    return (
        <div className={`${bg} ${border} border rounded-xl p-4 text-center`}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
            <p className={`text-2xl font-black mt-1 ${color}`}>{value}</p>
        </div>
    );
}